import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { LogIn, Snowflake, Truck, Construction, Flame, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { BigButton } from "@/components/BigButton";
import { ThemeToggle } from "@/context/ThemeContext";
import { LanguageSelect } from "@/components/LanguageSelect";
import { t } from "@/i18n";

const HERO = "https://images.unsplash.com/photo-1631744591853-998c4308bbb0?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";
const ICONS = { frigo: [Snowflake, "#4accff"], telonato: [Truck, "#ffffff"], eccezionale: [Construction, "#f59e0b"], adr: [Flame, "#ef0298"] };

export default function Login() {
  const { user, login, loginDemo } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [identifier, setId] = useState("");
  const [password, setPw] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/demo-drivers").then((r) => setDrivers(r.data)).catch(() => setDrivers([]));
  }, []);

  if (user) return <Navigate to="/" replace />;

  const pick = async (key) => {
    setBusy(true);
    setError("");
    try {
      await loginDemo(key);
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(identifier, password);
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block">
        <img src={HERO} alt={t("Camion in autostrada")} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0b0f17, rgba(11,15,23,0.4), transparent)" }} />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="nv-eyebrow text-[#4accff]">{t("Il navigatore dell'autista")}</p>
          <h1 className="font-display text-6xl font-extrabold uppercase leading-[0.95] mt-3" style={{ color: "#ffffff" }}>{t("Ordini, stato e strada.")}<br /><span className="text-[#ef0298]">{t("Tutto in un tocco.")}</span></h1>
        </div>
      </div>
      <div className="relative flex items-center justify-center p-6 pt-24 sm:p-12 sm:pt-24">
        <div className="absolute top-4 right-4 flex gap-2"><LanguageSelect /><ThemeToggle /></div>
        <div className="w-full max-w-md space-y-6 nv-rise">
          <Logo size={56} />
          <div>
            <h2 className="font-display text-4xl font-extrabold uppercase">{t("Accesso autista")}</h2>
            <p className="text-slate-400 mt-1">{t("Scegli il tuo profilo demo")}</p>
          </div>
          <div className="space-y-3" data-testid="demo-drivers">
            {drivers.map((d) => {
              const [Icon, color] = ICONS[d.key] || [Truck, "#4accff"];
              return <BigButton key={d.key} testId={`demo-driver-${d.key}`} icon={Icon} variant="outline" color={color} label={d.name} sub={t(d.vehicle)} disabled={busy} onClick={() => pick(d.key)} />;
            })}
          </div>
          {error && <p data-testid="login-error" className="rounded-xl bg-[#ef0298]/15 border border-[#ef0298] text-[#ff7cc8] px-4 py-3 font-semibold">{error}</p>}
          <button type="button" data-testid="toggle-credentials-btn" onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 text-sm text-slate-400 underline min-h-[44px]">
            <KeyRound className="h-4 w-4" /> {t(showForm ? "Nascondi accesso con credenziali" : "Accedi con email e password")}
          </button>
        {showForm && <form onSubmit={submit} className="space-y-6" data-testid="login-form">
          <label className="block">
            <span className="nv-eyebrow">{t("Email o nome utente")}</span>
            <input data-testid="login-identifier-input" value={identifier} onChange={(e) => setId(e.target.value)} autoComplete="username" required
              className="nv-input mt-2" placeholder={t("es. marco.rossini")} />
          </label>
          <label className="block">
            <span className="nv-eyebrow">{t("Password")}</span>
            <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" required
              className="nv-input mt-2" placeholder="••••••••" />
          </label>
          <button type="submit" disabled={busy} data-testid="login-submit-button"
            className="nv-press w-full min-h-[72px] rounded-2xl bg-[#ef0298] nv-on-accent font-display text-2xl font-extrabold uppercase tracking-wide flex items-center justify-center gap-3 disabled:opacity-60">
            <LogIn className="h-7 w-7" /> {busy ? t("Accesso…") : t("Entra")}
          </button>
        </form>}
        </div>
      </div>
    </div>
  );
}
